import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useAuth } from '@frontegg/nextjs';
import LogoutIcon from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import useLogout from '@pkg/auth/useLogout';
import config from '@/lib/config';
import { brand, core, grey } from '@/lib/theme/tokens/palettes';
import Logo from './Logo';
import Typography from './Typography';

const Root = styled('div')({
  backgroundColor: brand._navy.tints[0],
  position: 'relative',
  height: '100vh',
  width: '100vw',
});

const Content = styled('div')({
  backgroundColor: core.white,
  borderRadius: 8,
  boxShadow: `1px 1px 4px 0 ${grey.colors[100]}`,
  left: '50%',
  padding: 24,
  position: 'absolute',
  textAlign: 'center',
  top: 96,
  transform: 'translateX(-50%)',
  width: 704,
});

const AccountDisabled = () => {
  const router = useRouter();
  const { triggerLogout } = useLogout();
  const { tenantsState, user } = useAuth();
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    if (!tenantsState) {
      return;
    }
    const activeTenantId = user?.tenantId;
    const tenantNames = tenantsState.tenants
      .filter(({ tenantId }) => tenantId !== activeTenantId)
      .map(({ tenantId, name }) => ({ tenantId, name }));
    setTenants(tenantNames);
  }, [tenantsState, user?.tenantId]);

  const handleTenantChange = (event) => {
    const selectedTenantId = event.target.value;
    const matchingTenant = tenants.find(
      (tenant) => tenant.tenantId === selectedTenantId
    );

    if (matchingTenant) {
      window.location.href = `/a/${matchingTenant.tenantId}`;
    }
  };

  const handleLogout = async () => {
    await triggerLogout();
    setTimeout(() => {
      router.push(config.PATHS.LOGOUT);
    }, 2000);
  };

  return (
    <Root>
      <Content>
        <Logo type="full" sx={{ margin: 'auto', marginBottom: 1 }} />
        <Typography variant="subtitle1" sx={{ mb: 4, color: brand.coral.main }}>
          Account disabled
        </Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box
            sx={{
              flex: '0 0 66%',
              textAlign: 'left',
              borderRight: `1px solid ${grey.colors[200]}`,
              pr: 3,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              This account is disabled. Please consult your administrator for
              more information.
            </Typography>
            {tenants.length > 0 && (
              <Typography variant="body2">
                If you have access to other accounts you can select one on the
                right.
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: '0 0 calc(34% - 24px)' }}>
            {tenants.length > 0 && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 1,
                    textAlign: 'left',
                    fontWeight: 'bold',
                    display: 'block',
                  }}
                >
                  Access another account
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="switch-organisation-label">
                    Switch organisation
                  </InputLabel>
                  <Select
                    labelId="switch-organisation-label"
                    id="switch-organisation"
                    label="Switch organisation"
                    defaultValue=""
                    onChange={handleTenantChange}
                  >
                    {tenants.map(({ tenantId, name }) => (
                      <MenuItem key={tenantId} value={tenantId}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            <Button
              variant="contained"
              color="primary"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              fullWidth
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Content>
    </Root>
  );
};

export default AccountDisabled;
